import { useMemo } from "react";
import { SortDescriptor } from "react-aria-components";

import { USED_BY_COLUMN_ID } from "./constants";

type BaseUsedByItem = {
  id: string;
  name: string;
  type: string;
  spaceDetails: { name: string };
};

const sortUsedByItems = <T extends BaseUsedByItem>(
  items: T[],
  sortDescriptor: SortDescriptor
): T[] => {
  if (!sortDescriptor.column) {
    return items;
  }

  const isAscending = sortDescriptor.direction === "ascending";

  return [...items].sort((a, b) => {
    let valueA: string | undefined;
    let valueB: string | undefined;

    switch (sortDescriptor.column as USED_BY_COLUMN_ID) {
      case USED_BY_COLUMN_ID.TYPE:
        valueA = a.type;
        valueB = b.type;
        break;
      case USED_BY_COLUMN_ID.NAME:
        valueA = a.name;
        valueB = b.name;
        break;
      case USED_BY_COLUMN_ID.SPACE:
        valueA = a.spaceDetails.name;
        valueB = b.spaceDetails.name;
        break;
      case USED_BY_COLUMN_ID.DROPDOWN:
        break;
    }

    valueA = valueA || "";
    valueB = valueB || "";

    const comparison = isAscending ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    // Stable tie-breaker: without it, items with an equal sort value keep whatever
    // relative order they arrived in, which can visibly reshuffle between renders if
    // the input array's order isn't perfectly deterministic (e.g. after a re-fetch).
    return comparison !== 0 ? comparison : a.id.localeCompare(b.id);
  });
};

export const useFilteredUsedByItems = <T extends BaseUsedByItem>(
  items: T[],
  sortDescriptor: SortDescriptor,
  searchQuery: string
): T[] => {
  return useMemo(() => {
    let filteredItems = [...items];

    // Apply search filter if exists
    if (searchQuery) {
      filteredItems = filteredItems.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sorting if descriptor exists
    if (sortDescriptor.column) {
      filteredItems = sortUsedByItems(filteredItems, sortDescriptor);
    }

    return filteredItems;
  }, [items, searchQuery, sortDescriptor]);
};
